import React from 'react';
import axios from 'axios';

// Msg #774 — Cloaked-admin monitor panel for every private room in Mandala.
// Refreshes every 8s while open, lists room/owner/activity/last-message-at
// and offers a one-click "Shadow Join" button that hops the admin into that
// room without needing the password (backend enforces admin+cloak).
export const PrivateRoomMonitorModal = ({ open, apiBase, requester, cloaked, onJoin, onClose }) => {
  const [rooms, setRooms] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const refresh = React.useCallback(async () => {
    if (!open) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${apiBase}/admin/private_rooms`, { params: { requester } });
      setRooms(r.data.private_rooms || []);
    } catch (err) {
      const d = err && err.response && err.response.data && err.response.data.detail;
      setError(d || 'Failed to load private rooms.');
    }
    setLoading(false);
  }, [apiBase, requester, open]);

  React.useEffect(() => {
    if (!open) return;
    refresh();
    const id = setInterval(refresh, 8000);
    return () => clearInterval(id);
  }, [open, refresh]);

  if (!open) return null;

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); }
    catch (e) { void e; return iso; }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(20,40,20,0.55)',
        zIndex: 1050, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="private-room-monitor"
        style={{
          width: 680, maxWidth: '94vw',
          maxHeight: '85vh', overflowY: 'auto',
          background: '#d6e8c5',
          border: '2px solid #2d6b1e',
          boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
          padding: '18px 24px',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: '#1c3f1c',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ fontWeight: 'bold', fontSize: 14, flex: 1 }}>
            🥷 Private Rooms Monitor
          </div>
          <button
            data-testid="private-room-monitor-refresh"
            onClick={refresh}
            style={{
              background: '#1c3a1c', color: '#ecfcd9',
              border: '1px solid #2d6b1e',
              padding: '2px 10px', fontSize: 11, fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
          <button
            data-testid="private-room-monitor-close"
            onClick={onClose}
            style={{
              background: '#e9f3da',
              border: '1px solid #5b8c47',
              padding: '2px 10px', fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
        {!cloaked && (
          <div
            style={{
              fontSize: 11, color: '#7a1f1f',
              background: '#f3e6c8', padding: '6px 8px',
              border: '1px dashed #a02020', marginBottom: 8,
            }}
          >
            You are not currently cloaked. Uncloaked admins joining private
            rooms will still show up in the userlist.
          </div>
        )}
        {error && (
          <div style={{ color: '#a02020', fontSize: 11, marginBottom: 8 }}>{error}</div>
        )}
        {loading && rooms.length === 0 && (
          <div style={{ fontSize: 12, fontStyle: 'italic' }}>Loading…</div>
        )}
        {!loading && rooms.length === 0 && !error && (
          <div style={{ fontSize: 12, fontStyle: 'italic' }}>No private rooms exist.</div>
        )}
        {rooms.length > 0 && (
          <table
            data-testid="private-room-monitor-table"
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
          >
            <thead>
              <tr style={{ background: '#c7dfa9' }}>
                <th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #5b8c47' }}>Room</th>
                <th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #5b8c47' }}>Owner</th>
                <th style={{ textAlign: 'right', padding: '4px 6px', borderBottom: '1px solid #5b8c47' }}>Recent</th>
                <th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #5b8c47' }}>Last msg</th>
                <th style={{ padding: '4px 6px', borderBottom: '1px solid #5b8c47' }}></th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((r) => (
                <tr key={r.room} data-testid={`private-room-row-${r.room}`}>
                  <td style={{ padding: '4px 6px', borderBottom: '1px dotted #7a9c5c', fontWeight: 'bold' }}>{r.room}</td>
                  <td style={{ padding: '4px 6px', borderBottom: '1px dotted #7a9c5c' }}>{r.owner || '—'}</td>
                  <td style={{ padding: '4px 6px', borderBottom: '1px dotted #7a9c5c', textAlign: 'right' }}>{r.recent_message_count}</td>
                  <td style={{ padding: '4px 6px', borderBottom: '1px dotted #7a9c5c' }}>{fmt(r.last_message_at)}</td>
                  <td style={{ padding: '4px 6px', borderBottom: '1px dotted #7a9c5c' }}>
                    <button
                      data-testid={`private-room-join-${r.room}`}
                      onClick={() => { onJoin && onJoin(r.room); onClose(); }}
                      style={{
                        background: '#1c3a3a', color: '#dff5f5',
                        border: '1px solid #2d6b1e',
                        padding: '2px 8px', fontSize: 11, fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      Shadow join →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PrivateRoomMonitorModal;
